/**
 * `companies` — Settings → Company profile wire surface.
 *
 * Procedures:
 *   - `get`                 — read the active tenant's company row.
 *   - `update`              — patch the company (text fields).
 *   - `requestLogoUpload`   — phase 1 of the two-phase logo upload.
 *   - `confirmLogo`         — phase 2; flips logo_object_key after PUT.
 *
 * Mirrors the `identity.requestAvatarUpload` / `confirmAvatar` pair —
 * same two-phase pattern, same failure semantics, same wire shape (only
 * the table + upload prefix differ).
 */
import { z } from "zod";
import { uuid, gstin, pin, indianState, isoDate, phoneIN } from "../_common.js";

// ── Shared bits ───────────────────────────────────────────────────

/** Accent palette — must stay in sync with `domain ▸ types ACCENT_COLOURS`. */
export const accentColour = z.enum([
  "sky",
  "indigo",
  "emerald",
  "amber",
  "rose",
  "violet",
  "slate",
]);

/** Composition / SEZ / Casual / Regular — drives GST rate logic later. */
export const companyType = z.enum(["Regular", "Composition", "SEZ", "Casual"]);

/**
 * Invoice-composer behaviour for place-of-supply. Stored on
 * `companies.default_place_of_supply` (migration 0044).
 *
 * - `from_customer`        — auto-derive from customer GSTIN/state (default).
 * - `always_sender_state`  — pin to the company's registered state.
 * - `ask_per_invoice`      — clear on customer-pick; operator must choose.
 */
export const placeOfSupplyMode = z.enum([
  "from_customer",
  "always_sender_state",
  "ask_per_invoice",
]);
export type PlaceOfSupplyMode = z.infer<typeof placeOfSupplyMode>;

/**
 * Workspace-wide Flint AI language (migration 0105). Same code set as the
 * channel `VOICE_LANGUAGES` (see `bot-channels.ts`); kept as its own enum so
 * the company surface doesn't depend on the bot-channels module.
 */
export const aiLanguage = z.enum(["ml", "ta", "kn", "en", "hi"]);
export type AiLanguage = z.infer<typeof aiLanguage>;

/** PAN — exactly 10 chars, ABCDE1234F shape. */
export const pan = z.string().regex(/^[A-Z]{5}\d{4}[A-Z]$/, "Invalid PAN format.");

/** CIN — 21 chars, MCA spec. */
export const cin = z
  .string()
  .regex(/^[ULu][0-9]{5}[A-Za-z]{2}[0-9]{4}[A-Za-z]{3}[0-9]{6}$/, "Invalid CIN format.");

// ── get ───────────────────────────────────────────────────────────

export const getInput = z.object({});

/**
 * Full company shape exposed to the settings page. Fields are nullable
 * where the underlying column is nullable; `id` and `legalName` are the
 * only non-null fields (matches the schema's `notNull` columns).
 *
 * Read-side intentionally permissive on enum-shaped columns (`type`,
 * `accentColour`): the underlying `varchar` may legitimately hold older
 * free-form values from prior schema iterations (e.g. seed data that
 * pre-dates the enum constraint). The wire `updateInput` BELOW validates
 * strictly so any save normalises to the enum. Same idea for `email`
 * (already-stored values may not match the modern email regex).
 *
 * `logoUrl` is the resolved public URL (or null when no logo set / no
 * public host configured) — same pattern as `me.user.avatarUrl`.
 */
export const getOutput = z.object({
  id: uuid,
  legalName: z.string(),
  tradeName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  pan: z.string().nullable(),
  cin: z.string().nullable(),
  gstin: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  pincode: z.string().nullable(),
  address: z.string().nullable(),
  /** May hold legacy values; the form normalises to `Regular` for unknowns. */
  type: z.string(),
  /** Invoice-composer place-of-supply mode (migration 0044). */
  defaultPlaceOfSupply: z.string(),
  registeredOn: z.string().nullable(),
  /** May be null OR one of the enum values. */
  accentColour: z.string().nullable(),
  /** Public read URL for the logo, or null. */
  logoUrl: z.string().url().nullable(),
  /** PR-H — bank details printed on the customer-facing invoice. Public. */
  bankName: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankIfsc: z.string().nullable(),
  bankUpiId: z.string().nullable(),
  /** Export statutory credentials (migration 0115). */
  iec: z.string().nullable(),
  aepcRcmcNo: z.string().nullable(),
  adCode: z.string().nullable(),
  lutNo: z.string().nullable(),
  /** Default Declaration text printed on every invoice PDF (migration
   *  0035). Operator overrides per invoice via `invoices.declaration`.
   *  Null = use the hardcoded Section-16 LUT block for export invoices. */
  defaultInvoiceDeclaration: z.string().nullable(),
  /** Master notifications kill switch (migration 0088). When false the
   *  guardrail layer holds every transactional + system-alert send. */
  notificationsEnabled: z.boolean(),
  /** Master Flint AI switch (migration 0105). When false the agent entry
   *  points short-circuit instead of running a turn. */
  aiEnabled: z.boolean(),
  /** Workspace-wide assistant language (migration 0105). May hold any of the
   *  enum codes or null = auto-detect. Permissive on read (legacy values). */
  aiDefaultLanguage: z.string().nullable(),
});

// ── update ────────────────────────────────────────────────────────

/**
 * Every field optional. Locked-once-set columns (pan, cin, gstin) are
 * accepted only when their current value is null — the domain function
 * enforces this with `CompanyLockedFieldError → CONFLICT`.
 *
 * Empty-string on optional text fields rounds-trips to null (matches
 * the customer / user-profile pattern).
 */
export const updateInput = z
  .object({
    legalName: z.string().trim().min(1).max(255).optional(),
    tradeName: z
      .union([z.string().trim().max(255), z.literal("")])
      .nullable()
      .optional(),
    email: z
      .union([z.string().email(), z.literal("")])
      .nullable()
      .optional(),
    phone: z
      .union([phoneIN, z.literal("")])
      .nullable()
      .optional(),
    // Required on the customer-facing invoice (address block) and by
    // place-of-supply logic (state). Optional in the *payload* so partial
    // updates that don't touch these fields still validate — but when
    // present, must be non-empty. Diverges from the "empty string clears"
    // pattern used by tradeName/email/phone/etc; those are optional in the
    // product contract, these are not.
    address: z.string().trim().min(1, "Street address is required.").max(2048).optional(),
    city: z
      .union([z.string().trim().max(64), z.literal("")])
      .nullable()
      .optional(),
    state: indianState.optional(),
    pincode: z
      .union([pin, z.literal("")])
      .nullable()
      .optional(),
    type: companyType.optional(),
    defaultPlaceOfSupply: placeOfSupplyMode.optional(),
    registeredOn: z
      .union([isoDate, z.literal("")])
      .nullable()
      .optional(),
    accentColour: accentColour.optional(),
    pan: z
      .union([pan, z.literal("")])
      .nullable()
      .optional(),
    cin: z
      .union([cin, z.literal("")])
      .nullable()
      .optional(),
    gstin: z
      .union([gstin, z.literal("")])
      .nullable()
      .optional(),
    // PR-H — printed bank details. Plain text; not encrypted (the customer
    // needs to see them to pay).
    bankName: z
      .union([z.string().trim().max(64), z.literal("")])
      .nullable()
      .optional(),
    bankAccountNumber: z
      .union([
        z
          .string()
          .trim()
          .regex(/^\d{6,18}$/, "6–18 digit account number"),
        z.literal(""),
      ])
      .nullable()
      .optional(),
    bankIfsc: z
      .union([
        z
          .string()
          .trim()
          .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "invalid IFSC"),
        z.literal(""),
      ])
      .nullable()
      .optional(),
    bankUpiId: z
      .union([
        z
          .string()
          .trim()
          .regex(/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/, "invalid UPI id (e.g. name@bank)"),
        z.literal(""),
      ])
      .nullable()
      .optional(),
    // Export statutory credentials (migration 0115). Lenient formats; "" clears.
    iec: z
      .union([z.string().trim().max(10), z.literal("")])
      .nullable()
      .optional(),
    aepcRcmcNo: z
      .union([z.string().trim().max(40), z.literal("")])
      .nullable()
      .optional(),
    adCode: z
      .union([z.string().trim().max(20), z.literal("")])
      .nullable()
      .optional(),
    lutNo: z
      .union([z.string().trim().max(40), z.literal("")])
      .nullable()
      .optional(),
    /** Default invoice Declaration text. 4 kB cap is generous —
     *  declarations are 2-5 lines of boilerplate. Empty string clears it. */
    defaultInvoiceDeclaration: z
      .union([z.string().max(4000), z.literal("")])
      .nullable()
      .optional(),
    /** Master notifications kill switch (migration 0088). */
    notificationsEnabled: z.boolean().optional(),
    /** Master Flint AI switch (migration 0105). */
    aiEnabled: z.boolean().optional(),
    /** Workspace-wide assistant language (migration 0105). Empty string clears
     *  it to auto-detect (null). */
    aiDefaultLanguage: z
      .union([aiLanguage, z.literal("")])
      .nullable()
      .optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "Provide at least one field to update." });

export const updateOutput = z.object({
  companyId: uuid,
  /**
   * Fields the server actually wrote. Empty arrays cannot occur — the
   * domain throws `CompanyNoChangesError` (mapped to 400) before we get
   * here. The SPA uses this to drive an inline confirmation toast.
   */
  changedKeys: z
    .array(
      z.enum([
        "legalName",
        "tradeName",
        "email",
        "phone",
        "address",
        "city",
        "state",
        "pincode",
        "type",
        "defaultPlaceOfSupply",
        "registeredOn",
        "accentColour",
        "pan",
        "cin",
        "gstin",
        "bankName",
        "bankAccountNumber",
        "bankIfsc",
        "bankUpiId",
        "iec",
        "aepcRcmcNo",
        "adCode",
        "lutNo",
        "defaultInvoiceDeclaration",
        "notificationsEnabled",
        "aiEnabled",
        "aiDefaultLanguage",
      ]),
    )
    .min(1),
});

// ── requestLogoUpload ─────────────────────────────────────────────

export const requestLogoUploadInput = z.object({
  contentType: z.enum(["image/jpeg", "image/png", "image/webp", "image/svg+xml"]),
  /** 1 MiB cap — enforced server-side too. */
  size: z
    .number()
    .int()
    .positive()
    .max(1 * 1024 * 1024),
});

export const requestLogoUploadOutput = z.object({
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  maxBytes: z.number().int().positive(),
  contentType: z.enum(["image/jpeg", "image/png", "image/webp", "image/svg+xml"]),
});

// ── confirmLogo ───────────────────────────────────────────────────

export const confirmLogoInput = z.object({
  objectKey: z.string().min(1).max(512),
});

export const confirmLogoOutput = z.object({
  companyId: uuid,
  objectKey: z.string(),
  logoUrl: z.string().url().nullable(),
});
